import { TERRAIN_CHUNK_SIZE } from "@endclock/shared/world-bounds";
import { writeTerrainTransform, writeUprightTransform } from "../engine/math.js";

const FLOATS_PER_INSTANCE = 20;

export interface StaticInstanceObject {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly yaw: number;
  readonly scale: number;
  readonly tint: number;
  readonly chunkX: number;
  readonly chunkZ: number;
}

export interface StaticObjectChunk<T extends StaticInstanceObject> {
  readonly centerX: number;
  readonly centerZ: number;
  readonly centerY: number;
  readonly radius: number;
  readonly objects: readonly T[];
}

/** Persistent CPU-side instance storage. It only reallocates when a prior high-water mark is exceeded. */
export class ReusableInstanceBatch {
  data = new Float32Array(FLOATS_PER_INSTANCE * 8);
  count = 0;
  allocationCount = 1;

  reset(): void {
    this.count = 0;
  }

  pushUpright(
    object: StaticInstanceObject,
    color: readonly [number, number, number, number],
    minimumColor: number,
    maximumColor: number,
  ): void {
    const offset = this.reserve();
    writeUprightTransform(this.data, offset, object.x, object.y, object.z, object.yaw, object.scale);
    this.writeColor(offset, object.tint, color, minimumColor, maximumColor);
  }

  pushTerrainAligned(
    object: StaticInstanceObject,
    terrainNormal: readonly [number, number, number],
    normalAlignment: number,
    color: readonly [number, number, number, number],
    minimumColor: number,
    maximumColor: number,
  ): void {
    const offset = this.reserve();
    writeTerrainTransform(
      this.data,
      offset,
      object.x,
      object.y,
      object.z,
      object.yaw,
      object.scale,
      terrainNormal,
      normalAlignment,
    );
    this.writeColor(offset, object.tint, color, minimumColor, maximumColor);
  }

  private reserve(): number {
    const required = (this.count + 1) * FLOATS_PER_INSTANCE;
    if (required > this.data.length) {
      const next = new Float32Array(Math.max(required, this.data.length * 2));
      next.set(this.data);
      this.data = next;
      this.allocationCount += 1;
    }
    const offset = this.count * FLOATS_PER_INSTANCE;
    this.count += 1;
    return offset;
  }

  private writeColor(
    offset: number,
    tint: number,
    color: readonly [number, number, number, number],
    minimum: number,
    maximum: number,
  ): void {
    this.data[offset + 16] = clampColor(color[0] + tint, minimum, maximum);
    this.data[offset + 17] = clampColor(color[1] + tint * 0.82, minimum, maximum);
    this.data[offset + 18] = clampColor(color[2] + tint * 0.56, minimum, maximum);
    this.data[offset + 19] = color[3];
  }
}

export function createStaticObjectChunks<T extends StaticInstanceObject>(
  objects: readonly T[],
  boundsFor: (object: T) => { readonly centerY: number; readonly radius: number },
): readonly StaticObjectChunk<T>[] {
  const groups = new Map<string, T[]>();
  for (const object of objects) {
    const key = `${object.chunkX}:${object.chunkZ}`;
    const group = groups.get(key);
    if (group) group.push(object);
    else groups.set(key, [object]);
  }
  return [...groups.values()].map((group) => {
    const first = group[0]!;
    const centerX = (first.chunkX + 0.5) * TERRAIN_CHUNK_SIZE;
    const centerZ = (first.chunkZ + 0.5) * TERRAIN_CHUNK_SIZE;
    let minimumY = Number.POSITIVE_INFINITY;
    let maximumY = Number.NEGATIVE_INFINITY;
    const objectBounds = group.map((object) => ({ object, bounds: boundsFor(object) }));
    for (const { bounds } of objectBounds) {
      minimumY = Math.min(minimumY, bounds.centerY - bounds.radius);
      maximumY = Math.max(maximumY, bounds.centerY + bounds.radius);
    }
    const centerY = (minimumY + maximumY) * 0.5;
    let radius = 0;
    for (const { object, bounds } of objectBounds) {
      radius = Math.max(
        radius,
        Math.hypot(object.x - centerX, bounds.centerY - centerY, object.z - centerZ) + bounds.radius,
      );
    }
    return {
      centerX,
      centerZ,
      centerY,
      radius,
      objects: group,
    };
  });
}

function clampColor(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}
